import axios from "axios";
import { Paperclip, SendHorizonal } from "lucide-react";
import type { Node as ProseMirrorNode } from "prosemirror-model";
import { useCallback, useRef, useState } from "react";
import { useDropzone } from "react-dropzone";
import { useHotkeys } from "react-hotkeys-hook";
import { taskFileUrl } from "@/lib/image-urls";
import { trpcClient } from "@/lib/trpc";
import { isApplePlatform } from "@/lib/utils";
import ChatEditor, { type ChatEditorRef } from "../text-editor/ChatEditor";
import { Button } from "../ui/button";
import { Tooltip, TooltipContent, TooltipTrigger } from "../ui/tooltip";

export default function NewChatMessage({
    taskId,
    onSubmit,
}: {
    taskId: number;
    onSubmit: (json: Record<string, any> | null, html: string | null) => void;
}) {
    const [json, setJson] = useState<Record<string, any> | null>(null);
    const [html, setHtml] = useState<string | null>(null);
    const [uploadingFiles, setUploadingFiles] = useState<Record<string, boolean>>({});
    const editorRef = useRef<ChatEditorRef>(null);
    const isUploading = Object.keys(uploadingFiles).length > 0;

    useHotkeys("mod+enter", () => handleSubmit(), {
        preventDefault: true,
        enableOnContentEditable: true,
        enableOnFormTags: true,
    });

    const handleFileUpload = useCallback(
        async (file: File): Promise<{ finalUrl: string; finalFileName: string; id: string }> => {
            const contentType = file.type;
            // Create a unique identifier for this upload
            const uploadId = `${file.name}-${Date.now()}`;
            setUploadingFiles((prev) => ({ ...prev, [uploadId]: true }));

            // Create a temporary preview URL
            const previewUrl = URL.createObjectURL(file);

            // Upload file to server
            const {
                url,
                id,
                fileName: finalFileName,
            } = await trpcClient.upload.getPresignedTaskFileUrl.query({ contentType, taskId });
            await axios.put(url, file, {
                headers: {
                    "Content-Type": contentType,
                },
            });
            const finalUrl = taskFileUrl({ taskId, filename: finalFileName, contentType });

            // Clean up the preview URL
            URL.revokeObjectURL(previewUrl);

            setUploadingFiles((prev) => {
                const newState = { ...prev };
                delete newState[uploadId];
                return newState;
            });

            return { finalUrl, finalFileName, id };
        },
        [taskId]
    );

    const handleSubmit = () => {
        if (isUploading) return;
        if (html === "<p><br></p>") return;

        onSubmit(json, html);
        setJson(null);
        if (editorRef.current?.editor) {
            editorRef.current.editor.commands.clearContent();
        }
    };

    const insertMedia = useCallback(
        async (file: File): Promise<string> => {
            if (!editorRef.current?.editor || !onSubmit) return "";

            try {
                // Create a temporary preview URL and insert it
                const previewUrl = URL.createObjectURL(file);
                const fileName = file.name;
                const contentType = file.type;

                // Insert the media with preview URL
                editorRef.current.editor.commands.addOrsaneMedia({
                    url: previewUrl,
                    fileName,
                    contentType,
                    id: "",
                    href: "",
                });

                // Upload the file and get the final URL
                const { finalUrl, finalFileName, id } = await handleFileUpload(file);

                // Update all orsanemedia nodes that have the preview URL with the final URL
                editorRef.current.editor.state.doc.descendants((node: ProseMirrorNode, pos: number) => {
                    if (node.type.name === "orsanemedia" && node.attrs.url === previewUrl) {
                        if (!editorRef.current?.editor) {
                            return;
                        }
                        // Create a transaction to update the node
                        const { tr } = editorRef.current.editor.state;

                        // Create new attributes by spreading the existing ones and updating the url
                        const newAttrs = {
                            ...node.attrs,
                            url: finalUrl,
                            href: taskFileUrl({ taskId, filename: finalFileName, contentType, fullWidth: true }),
                            id,
                        };

                        // Set the new attributes at the node's position
                        tr.setNodeMarkup(pos, null, newAttrs);

                        // Dispatch the transaction to update the editor
                        editorRef.current.editor.view.dispatch(tr);
                    }
                });

                // Clean up the preview URL
                URL.revokeObjectURL(previewUrl);

                return finalUrl;
            } catch (error) {
                console.error("Error handling media upload:", error);
                throw error;
            }
        },
        [handleFileUpload, onSubmit, taskId]
    );

    const onDrop = useCallback(
        async (acceptedFiles: File[]) => {
            if (!editorRef.current?.editor) return;

            const promises = acceptedFiles.map((file) => insertMedia(file));
            await Promise.all(promises);
        },
        [insertMedia]
    );

    const { getRootProps, getInputProps, isDragActive, open } = useDropzone({
        onDrop,
        noClick: true,
        noKeyboard: true,
        accept: {
            "image/*": [".png", ".jpg", ".jpeg", ".gif"],
            "application/pdf": [".pdf"],
            "application/msword": [".doc"],
            "application/vnd.openxmlformats-officedocument.wordprocessingml.document": [".docx"],
            "text/plain": [".txt"],
        },
    });

    return (
        <div className="relative">
            {/** biome-ignore lint/a11y/noStaticElementInteractions: it's just for the focus */}
            <div
                {...getRootProps()}
                className="flex flex-col border border-input rounded-md focus-within:border-ring focus-within:ring-ring/50 focus-within:ring-[3px] relative cursor-text"
                onMouseDown={(e) => {
                    e.stopPropagation();
                    editorRef.current?.editor?.commands.focus();
                }}
            >
                {/** biome-ignore lint/a11y/noStaticElementInteractions: For the focussss */}
                {/** biome-ignore lint/a11y/useKeyWithClickEvents: For the focussss */}
                <div className="p-2 max-h-[200px] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
                    <ChatEditor
                        ref={editorRef}
                        value={json}
                        onChange={(json, html) => {
                            setJson(json);
                            setHtml(html);
                        }}
                        onPasteFile={insertMedia}
                    />
                </div>

                <div className="flex items-center justify-between p-2 pt-0">
                    <div className="">
                        <div className="hidden">
                            <input {...getInputProps()} />
                        </div>
                    </div>
                    <div className="flex gap-2 ml-auto">
                        <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            className="size-7"
                            onClick={(e) => {
                                e.stopPropagation();
                                open();
                            }}
                        >
                            <Paperclip className="size-4" />
                        </Button>
                        <Tooltip>
                            <TooltipTrigger
                                render={
                                    <Button
                                        type="button"
                                        variant="default"
                                        size="icon"
                                        className="size-7"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            handleSubmit();
                                        }}
                                        disabled={isUploading || !json || Object.keys(json).length === 0 || html === "<p><br></p>"}
                                    >
                                        <SendHorizonal className="size-4" />
                                    </Button>
                                }
                            ></TooltipTrigger>
                            <TooltipContent>{isApplePlatform() ? "⌘+Enter" : "Ctrl+Enter"}</TooltipContent>
                        </Tooltip>
                    </div>
                </div>
                {isDragActive && (
                    <div className="absolute inset-0 z-50 bg-neutral-200 border-2 border-dashed border-primary rounded-md flex items-center justify-center pointer-events-none">
                        <p className="text-primary font-medium">Drop files here...</p>
                    </div>
                )}
            </div>
        </div>
    );
}
